import { motion } from "framer-motion";
import { useLang } from "../lang";
import { SITE, CERTIFICATIONS, NUMBERS } from "../content";
import Breadcrumb from "../components/Breadcrumb";
import BrandEyebrow from "../components/BrandEyebrow";
import HGWSymbol from "../components/HGWSymbol";
import { Link } from "react-router-dom";

export default function ChiSiamo() {
  const { t, lang } = useLang();
  return (
    <main className="min-h-screen bg-white pt-20" data-testid="chi-siamo-page">
      <div className="max-w-[1200px] mx-auto px-6 pt-8">
        <Breadcrumb items={[{ label: "Home", to: "/" }, { label: lang === "it" ? "Chi siamo" : "About" }]} />
      </div>

      {/* Header */}
      <section className="pt-8 pb-4">
        <div className="max-w-[900px] mx-auto px-6 text-center">
          <div className="mb-5"><BrandEyebrow>{t.introEyebrow}</BrandEyebrow></div>
          <h1 className="text-[36px] md:text-[56px] lg:text-[64px] font-semibold tracking-[-0.02em] text-[#1d1d1f] leading-[1.05]">
            {t.introTitle}
          </h1>
          <p className="mt-6 text-[17px] md:text-[19px] text-[#1d1d1f]/80 leading-[1.6]" data-testid="about-p1">
            {t.introBody}
          </p>
          <p className="mt-5 text-[17px] md:text-[19px] text-[#1d1d1f]/80 leading-[1.6]" data-testid="about-p2">
            {t.introBody2}
          </p>
          <p className="mt-5 text-[17px] md:text-[19px] text-[#1d1d1f]/80 leading-[1.6]" data-testid="about-p3">
            {t.introBody3}
          </p>
          <p className="mt-5 text-[17px] md:text-[19px] text-[#1d1d1f]/80 leading-[1.6]" data-testid="about-p4">
            {t.introBody4}
          </p>
          <p className="mt-5 text-[17px] md:text-[19px] text-[#1d1d1f]/80 leading-[1.6]" data-testid="about-p5">
            {t.introBody5}
          </p>
          <p
            className="mt-10 text-[19px] md:text-[24px] font-semibold tracking-[-0.01em] text-[#1D3F94]"
            data-testid="about-signature"
          >
            {t.introSignature}
          </p>
        </div>
      </section>

      {/* Image - HGW Industry headquarters */}
      <section className="mt-10 md:mt-16">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="relative rounded-3xl overflow-hidden">
            <img
              src="https://customer-assets-lqy194kg.emergentagent.net/job_asas-profili-dist/artifacts/0vgk75zp_sede.webp"
              alt="Sede HGW Industry — Somma Vesuviana (NA)"
              className="w-full h-auto block"
              loading="lazy"
              data-testid="about-hq-image"
            />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent p-6 md:p-10">
              <div className="text-[11px] uppercase tracking-[0.18em] text-white/80 font-medium">
                {lang === "it" ? "La nostra sede" : "Our headquarters"}
              </div>
              <div className="mt-1 text-white text-[18px] md:text-[22px] font-semibold tracking-[-0.01em]">
                Somma Vesuviana · Napoli
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="section-pad">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center max-w-[1040px] mx-auto mb-12">
            <div className="mb-5"><BrandEyebrow>{t.mvEyebrow}</BrandEyebrow></div>
            <h2 className="text-[32px] md:text-[48px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">
              {t.mvTitle}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="rounded-3xl bg-[#f5f5f7] p-8 md:p-12"
              data-testid="about-mission"
            >
              <div className="text-[12px] uppercase tracking-widest text-[#6e6e73] mb-6">{t.missionLabel}</div>
              <ul className="space-y-4 max-w-[440px]">
                {(Array.isArray(t.missionBody) ? t.missionBody : [t.missionBody]).map((line, li) => (
                  <li key={li} className="flex items-start gap-3 text-[17px] md:text-[19px] leading-[1.45] font-medium tracking-[-0.005em] text-[#1d1d1f]">
                    <HGWSymbol size={16} className="mt-[5px] shrink-0 text-[#1D3F94]" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="rounded-3xl bg-[#0b0f1a] text-white p-8 md:p-12"
              data-testid="about-vision"
            >
              <div className="text-[12px] uppercase tracking-widest text-white/60 mb-6">{t.visionLabel}</div>
              <ul className="space-y-4 max-w-[440px]">
                {(Array.isArray(t.visionBody) ? t.visionBody : [t.visionBody]).map((line, li) => (
                  <li key={li} className="flex items-start gap-3 text-[17px] md:text-[19px] leading-[1.45] font-medium tracking-[-0.005em] text-white">
                    <HGWSymbol size={16} className="mt-[5px] shrink-0 text-white/80" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Numbers */}
      <section className="section-pad-sm bg-[#f5f5f7]">
        <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {NUMBERS.map((n, i) => (
            <div key={n.id} className="rounded-3xl bg-white p-6 md:p-8 text-center" data-testid={`about-stat-${i}`}>
              <div className="text-[36px] md:text-[54px] font-semibold text-[#1D3F94] leading-none tracking-[-0.02em]">
                {n.value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")}{n.suffix}
              </div>
              <div className="mt-3 text-[13px] text-[#6e6e73]">{lang === "it" ? n.it : n.en}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Certifications */}
      <section className="section-pad" data-testid="about-certifications">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center max-w-[720px] mx-auto mb-12">
            <div className="mb-5"><BrandEyebrow>{t.certEyebrow}</BrandEyebrow></div>
            <h2 className="text-[32px] md:text-[44px] font-semibold tracking-[-0.02em] text-[#1d1d1f] leading-[1.1]">
              {t.certTitle}
            </h2>
            <p className="mt-4 text-[15px] md:text-[17px] text-[#1d1d1f]/70 leading-relaxed">
              {t.certBody}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CERTIFICATIONS.map((c, i) => {
              const active = c.status === "active";
              return (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className={`relative rounded-2xl p-6 md:p-7 border ${active ? "bg-white border-[#1D3F94]/25" : "bg-[#f5f5f7] border-transparent"}`}
                  data-testid={`certification-${c.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center ${active ? "bg-[#1D3F94] text-white" : "bg-white text-[#6e6e73] border border-[#e4e4e7]"}`}>
                      {active ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      )}
                    </div>
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-medium uppercase tracking-[0.08em] ${active ? "bg-[#1D3F94]/10 text-[#1D3F94]" : "bg-white text-[#6e6e73] border border-[#e4e4e7]"}`}
                      data-testid={`cert-status-${c.id}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-[#1D3F94]" : "bg-[#a1a1aa]"}`} />
                      {active ? t.certStatusActive : t.certStatusInProgress}
                    </span>
                  </div>
                  <div className="text-[19px] font-semibold tracking-[-0.01em] text-[#1d1d1f]">{c.label}</div>
                  <p className="mt-2 text-[13px] text-[#1d1d1f]/70 leading-relaxed">
                    {lang === "it" ? c.it : c.en}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad text-center">
        <div className="max-w-[820px] mx-auto px-6">
          <h2 className="text-[28px] md:text-[40px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">
            {lang === "it" ? "Vieni a trovarci." : "Come and see us."}
          </h2>
          <p className="mt-3 text-[17px] text-[#1d1d1f]/80">
            {lang === "it"
              ? "Il nostro showroom è a Somma Vesuviana. Prenota un appuntamento e tocca con mano i nostri sistemi."
              : "Our showroom is in Somma Vesuviana. Book an appointment and try our systems in person."}
          </p>
          <div className="mt-7 flex items-center justify-center gap-6">
            <Link to="/contatti" className="btn-pill btn-primary">
              {lang === "it" ? "Prenota una visita" : "Book a visit"}
            </Link>
            <Link to="/cataloghi" className="a-link" data-testid="about-cta-catalogs">
              {lang === "it" ? "Sfoglia i cataloghi" : "Browse catalogues"} <span className="chev">›</span>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
